import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Mail, DollarSign, Search, ExternalLink, Clock, Trash2, Flame, ArrowDownAZ, Sparkles, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const STATUSES = [
  { value: "new", label: "New", cls: "bg-gold/15 text-[#8A6F2A] border-gold/40" },
  { value: "contacted", label: "Contacted", cls: "bg-navy/5 text-navy/70 border-navy/20" },
  { value: "converted", label: "Converted", cls: "bg-navy text-ivory border-navy" },
  { value: "converted_to_intake", label: "→ Intake", cls: "bg-gold text-navy border-gold" },
  { value: "dead", label: "Dead", cls: "bg-navy/5 text-navy/40 border-navy/10 line-through" },
];
const statusCls = (s) => STATUSES.find((x) => x.value === s)?.cls || STATUSES[0].cls;

const TIER_STYLE = {
  hot: { bg: "bg-red-500/10", text: "text-red-700", border: "border-red-500/30", dot: "bg-red-500", label: "🔥 HOT" },
  warm: { bg: "bg-gold/15", text: "text-[#8A6F2A]", border: "border-gold/40", dot: "bg-gold", label: "🔆 WARM" },
  cold: { bg: "bg-navy/5", text: "text-navy/50", border: "border-navy/15", dot: "bg-navy/30", label: "❄ COLD" },
};
const tierStyle = (t) => TIER_STYLE[t] || TIER_STYLE.cold;

export default function LeadsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [sort, setSort] = useState("score"); // "score" | "newest"
  const [convertingId, setConvertingId] = useState(null);
  const nav = useNavigate();

  const refresh = () => {
    setLoading(true);
    api.get("/leads", { params: { sort } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [sort]);

  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const deleteLead = async (lead) => {
    const label = lead.business_name || lead.email || "this lead";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      setItems((prev) => prev.filter((x) => x.id !== lead.id));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const convertToIntake = async (lead) => {
    setConvertingId(lead.id);
    try {
      const r = await api.post(`/leads/${lead.id}/convert-to-interview`);
      if (r.data?.reused) {
        toast.info("This lead already has an intake — opening it.");
      } else {
        toast.success("Intake created from lead.");
      }
      nav(`/interviews/${r.data.interview_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed");
    } finally {
      setConvertingId(null);
    }
  };

  const filtered = items.filter((l) => {
    const hay = `${l.name} ${l.email} ${l.business_name} ${l.industry} ${l.top_pain}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const totals = {
    total: items.length,
    new: items.filter((i) => i.status === "new").length,
    converted: items.filter((i) => i.paid).length,
    revenue: items.filter((i) => i.paid).length * 1000,
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="eyebrow text-gold">Pipeline</div>
          <h1 className="font-display text-5xl text-navy mt-3">Leads</h1>
          <div className="w-12 h-[2px] bg-gold mt-4" />
          <p className="text-navy/60 mt-4 max-w-2xl">
            Freemium submissions captured from your public free-assessment page. Follow up, track status, and close.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Total Leads" value={totals.total} />
          <Stat label="New (Untouched)" value={totals.new} />
          <Stat label="Converted" value={totals.converted} />
          <Stat label="Revenue" value={`$${totals.revenue.toLocaleString()}`} highlight />
        </div>

        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
            <Input data-testid="leads-search" placeholder="Search name, email, business..."
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-10 rounded-none bg-white border-navy/15" />
          </div>
          <div className="flex border border-navy/15 bg-white">
            <button
              onClick={() => setSort("score")}
              data-testid="sort-by-score"
              className={`px-4 h-10 text-xs uppercase tracking-wider flex items-center gap-1.5 transition ${sort === "score" ? "bg-navy text-ivory" : "text-navy/60 hover:text-navy"}`}>
              <Flame className="w-3.5 h-3.5" /> Hottest
            </button>
            <button
              onClick={() => setSort("newest")}
              data-testid="sort-by-newest"
              className={`px-4 h-10 text-xs uppercase tracking-wider flex items-center gap-1.5 border-l border-navy/15 transition ${sort === "newest" ? "bg-navy text-ivory" : "text-navy/60 hover:text-navy"}`}>
              <ArrowDownAZ className="w-3.5 h-3.5" /> Newest
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-navy/50">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-navy/10 p-16 text-center">
            <Mail className="w-10 h-10 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-navy">No leads yet</h3>
            <p className="text-navy/60 mt-2">Share your free-assessment URL to start capturing leads.</p>
            <code className="block bg-ivory border border-navy/10 p-3 mt-6 text-sm text-navy inline-block">
              {window.location.origin}/free-assessment
            </code>
          </div>
        ) : (
          <div className="bg-white border border-navy/10" data-testid="leads-list">
            {filtered.map((l) => (
              <div key={l.id} className="border-b border-navy/5 last:border-b-0" data-testid={`lead-row-${l.id}`}>
                <div className="grid grid-cols-12 gap-4 p-5 items-start">
                  <div className="col-span-12 md:col-span-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-lg text-navy">{l.business_name}</div>
                        <div className="text-sm text-navy/60 mt-0.5">{l.name}</div>
                      </div>
                      {(() => {
                        const ts = tierStyle(l.tier || "cold");
                        return (
                          <div className={`flex flex-col items-center px-2.5 py-1.5 border ${ts.bg} ${ts.text} ${ts.border} flex-shrink-0`}
                            data-testid={`lead-tier-${l.id}`}>
                            <div className="font-display font-bold text-lg leading-none">{l.score ?? 0}</div>
                            <div className="text-[9px] tracking-[0.15em] mt-0.5">{ts.label}</div>
                          </div>
                        );
                      })()}
                    </div>
                    <a href={`mailto:${l.email}`} className="text-sm text-gold hover:underline inline-flex items-center gap-1 mt-1.5">
                      <Mail className="w-3 h-3" /> {l.email}
                    </a>
                    <div className="text-xs text-navy/50 mt-1">{l.industry || "—"} · {l.team_size || "team?"}</div>
                  </div>
                  <div className="col-span-12 md:col-span-4 text-sm text-navy/70">
                    <div className="eyebrow text-navy/40 mb-1">Top Pain</div>
                    <p className="line-clamp-3">{l.top_pain || "—"}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="eyebrow text-navy/40 mb-1">Cost of Inaction</div>
                    <div className="font-display text-lg text-navy">
                      ${(l.report?.executive_snapshot?.annual_cost_of_inaction || l.report?.monthly_roi_estimate || 0).toLocaleString()}
                      <span className="text-xs text-navy/50">/yr</span>
                    </div>
                    <div className="text-xs text-navy/50 flex items-center mt-1">
                      <Clock className="w-3 h-3 mr-1" /> {new Date(l.created_at).toLocaleDateString()}
                    </div>
                    {l.paid && <Badge className="mt-2 bg-gold text-navy rounded-none"><DollarSign className="w-3 h-3 mr-1" />Paid</Badge>}
                  </div>
                  <div className="col-span-6 md:col-span-2 space-y-2">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-${l.id}`} className={`rounded-none ${statusCls(l.status)} text-[10px] font-semibold uppercase tracking-wider h-8`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                      data-testid={`expand-${l.id}`}
                      className="w-full text-xs text-navy/60 hover:text-gold py-1 flex items-center justify-center gap-1">
                      {expanded === l.id ? "Hide report" : "View report"} <ExternalLink className="w-3 h-3" />
                    </button>
                    <button onClick={() => convertToIntake(l)}
                      data-testid={`convert-to-intake-${l.id}`}
                      disabled={convertingId === l.id}
                      className="w-full text-xs text-navy hover:text-gold py-1 flex items-center justify-center gap-1 border-t border-navy/5 disabled:opacity-50">
                      {convertingId === l.id ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Converting…</>
                      ) : (
                        <><Sparkles className="w-3 h-3 text-gold" /> Convert to Intake</>
                      )}
                    </button>
                    <button onClick={() => deleteLead(l)}
                      data-testid={`delete-lead-${l.id}`}
                      className="w-full text-xs text-navy/40 hover:text-red-600 py-1 flex items-center justify-center gap-1 border-t border-navy/5">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
                {expanded === l.id && (
                  <div className="px-5 pb-5 bg-ivory/50">
                    <div className="p-5 bg-white border border-navy/10">
                      <div className="eyebrow text-gold">Snapshot</div>
                      <h4 className="font-display text-xl text-navy mt-1">{l.report?.headline}</h4>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="bg-navy text-ivory p-3 text-center">
                          <div className="font-display text-2xl text-gold">{l.report?.hours_saved_weekly_estimate}</div>
                          <div className="text-[10px] tracking-widest">HRS/WK</div>
                        </div>
                        <div className="bg-gold text-navy p-3 text-center">
                          <div className="font-display text-2xl">${(l.report?.monthly_roi_estimate || 0).toLocaleString()}</div>
                          <div className="text-[10px] tracking-widest">MONTHLY ROI</div>
                        </div>
                      </div>
                      <div className="mt-4 space-y-1.5">
                        {(l.report?.top_3_tools || []).map((t, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-navy">{t.name}</span>
                            <span className="text-navy/60">${t.monthly_cost}/mo</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm italic text-navy/70 mt-3 border-l-2 border-gold pl-3">"{l.report?.one_bold_next_step}"</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-5 border ${highlight ? "bg-navy text-ivory border-navy" : "bg-white border-navy/10"}`}>
      <div className={`eyebrow ${highlight ? "text-gold" : "text-navy/50"}`}>{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${highlight ? "text-gold" : "text-navy"}`}>{value}</div>
    </div>
  );
}
